/*
Button component attributes:

size [string]: button size (ex: 'large', 'medium')
variant [string]: variant name (ex: 'primary-white', 'secondary', 'tertiary')
rounded [boolean]: if true, the button will have rounded edges
text [string]: the text to display
type [string]: the button type (ex: 'button', 'submit')
iconLeft [mithril element]: icon displayed to the left of the text
iconRight [mithril element]: icon displayed to the left of the text
addClasses [string | string[]]: list of classnames to add to the button element
onclick [function]: callback handler for the onclick event
disabled [boolean]: the disabled state of the button
loading [boolean]: the loading state of the button
loadingText [string]: the text to display while loading
loadingBlackDots [boolean]: use black color for the loader animation dots
*/

import m from 'mithril'
import clsx from 'clsx'
import LoaderDotPulse from '@app/components/loader-dot-pulse'

import s from './button.module.scss'
import { initMithrilComponent } from '@client-shared/utils/mithril'

export const BUTTON_VARIANTS = {
	PRIMARY: 'primary-blue',
	PRIMARY_WHITE: 'primary-white',
	SECONDARY: 'secondary',
	TERTIARY: 'tertiary',
	QUATERNARY: 'quaternary',
	GHOST: 'ghost',
	DANGER: 'danger',
	DANGER_GHOST: 'danger-ghost',
	DANGER_GHOST_ON_HOVER: 'danger-ghost-on-hover',
	LINK: 'link',
	LINK_UNDERLINE: 'link-underline',
}

const default_onclick_handler = () => console.warn('onclick handler not assigned to button')

interface IButtonAttrs {
	size?: 'large' | 'medium' | 'small' | 'icon' | 'slim'
	onclick?: (e: MouseEvent) => void
	text?: string
	variant?: string
	rounded?: boolean
	type?: string
	iconLeft?: JSX.Element
	iconRight?: JSX.Element
	addClasses?: string
	disabled?: boolean
	loading?: boolean
	loadingText?: string
	loadingBlackDots?: boolean
	style?: string
}

const Button = initMithrilComponent<IButtonAttrs>()

Button.view = (vnode) => {
	const {
		size = 'large',
		variant = BUTTON_VARIANTS.PRIMARY,
		rounded = true,
		type = 'button',
		iconLeft = null,
		iconRight = null,
		addClasses = null,
		disabled = false,
		loading = false,
		loadingText = 'Loading',
		loadingBlackDots = false,
		style,
	} = vnode.attrs
	const { onclick = type === 'button' ? default_onclick_handler : undefined } = vnode.attrs
	let { text = '' } = vnode.attrs

	if (!text && Array.isArray(vnode.children) && vnode.children.length && typeof vnode.children[0] === 'string') {
		text = vnode.children[0]
	}

	let classes = [s.button]

	switch (variant) {
		case BUTTON_VARIANTS.PRIMARY_WHITE:
			classes.push(s.variant__primary_white)
			break

		case BUTTON_VARIANTS.SECONDARY:
			classes.push(s.variant__secondary)
			break

		case BUTTON_VARIANTS.TERTIARY:
		case BUTTON_VARIANTS.GHOST:
			classes.push(s.variant__tertiary)
			break

		case BUTTON_VARIANTS.QUATERNARY:
			classes.push(s.variant__quaternary)
			break

		case BUTTON_VARIANTS.DANGER:
			classes.push(s.variant__danger)
			break

		case BUTTON_VARIANTS.DANGER_GHOST:
			classes.push(s.variant__danger_ghost)
			break

		case BUTTON_VARIANTS.DANGER_GHOST_ON_HOVER:
			classes.push(s.variant__danger_ghost_on_hover)
			break

		case BUTTON_VARIANTS.LINK:
			classes.push(s.variant__link)
			break

		case BUTTON_VARIANTS.LINK_UNDERLINE:
			classes.push(s.variant__link_underline)
			break

		default:
			break
	}

	switch (size) {
		case 'small':
			classes.push(s.size__small)
			break

		case 'medium':
			classes.push(s.size__medium)
			break

		case 'slim':
			classes.push(s.size__slim)
			break

		case 'icon':
			classes.push(s.size__icon)
			break

		default:
			break
	}

	if (rounded) {
		classes.push(s.rounded)
	}

	if (addClasses) {
		if (Array.isArray(addClasses)) {
			classes = classes.concat(addClasses)
		} else {
			classes.push(addClasses)
		}
	}

	if (disabled) {
		classes.push(s.disabled)
	}

	if (loading) {
		classes.push(s.loading)
	}

	if (size === 'icon' && !loadingText && loading) {
		classes.push(s.button_with_only_loading_icon)
	}

	return m(
		'button',
		{
			type,
			class: classes.join(' '),
			onclick: disabled || loading ? undefined : onclick,
			disabled,
			style,
		},
		loading
			? [m('span', { class: s.button_text }, loadingText), m(LoaderDotPulse, { black: loadingBlackDots })]
			: [
					iconLeft,
					m(
						'span',
						{
							class: clsx(s.button_text, {
								[s.button_text_icon]: size === 'icon',
							}),
						},
						text,
					),
					iconRight,
				],
	)
}

export default Button
